import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Essays", "Craft & Platform", "Culture", "Literary Projects", "Theater & Performance", "Exhibitions", "Community Storytelling", "News"];

const inputClass =
  "w-full bg-white border border-[#e5e5e5] text-[#121212] px-4 py-3 text-sm focus:border-[#121212] focus:outline-none rounded-none";
const labelClass = "block text-xs font-mono uppercase tracking-[.14em] text-[#555555] mb-2";

const AdminBlogEditPage = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Essays",
    tags: "",
    featured_image: "",
    author: "#whatshesaidproject",
    status: "draft",
    published_at: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/admin/blog/${id}`, { withCredentials: true }).then((res) => {
        setForm({
          ...res.data,
          tags: (res.data.tags || []).join(", "),
          published_at: res.data.published_at || "",
        });
      });
    }
  }, [id, isNew]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function save(status) {
    setBusy(true);
    try {
      const payload = {
        ...form,
        status,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        published_at: form.published_at || null,
      };
      if (isNew) {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/blog/${id}`, payload, { withCredentials: true });
      }
      toast.success(status === "published" ? "Post published." : "Draft saved.");
      navigate("/admin/blog");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the post.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-blog-edit">
      <Link
        to="/admin/blog"
        className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] hover:text-[#e02020] inline-block mb-6"
      >
        ← Back to Posts
      </Link>
      <h1 className="font-disp text-3xl text-[#121212] mb-8">
        {isNew ? "New Post" : "Edit Post"}
      </h1>

      <div className="bg-white border border-[#e5e5e5] p-8 space-y-6">
        <div>
          <label htmlFor="post-title" className={labelClass}>Title</label>
          <input id="post-title" data-testid="post-title-input" type="text" required value={form.title} onChange={update("title")} className={inputClass} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="post-slug" className={labelClass}>URL Slug (optional — auto from title)</label>
            <input id="post-slug" data-testid="post-slug-input" type="text" value={form.slug} onChange={update("slug")} placeholder="my-first-essay" className={inputClass} />
          </div>
          <div>
            <label htmlFor="post-category" className={labelClass}>Category</label>
            <select id="post-category" data-testid="post-category-select" value={form.category} onChange={update("category")} className={inputClass}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="post-excerpt" className={labelClass}>Excerpt</label>
          <textarea id="post-excerpt" data-testid="post-excerpt-textarea" rows={2} value={form.excerpt} onChange={update("excerpt")} className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Body</label>
          <RichTextEditor
            value={form.content}
            onChange={(html) => setForm({ ...form, content: html })}
            placeholder="Write the post…"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="post-image" className={labelClass}>Featured Image URL</label>
            <input id="post-image" data-testid="post-image-input" type="url" value={form.featured_image} onChange={update("image_url" in form ? "featured_image" : "featured_image")} placeholder="https://…" className={inputClass} />
          </div>
          <div>
            <label htmlFor="post-tags" className={labelClass}>Tags (comma separated)</label>
            <input id="post-tags" data-testid="post-tags-input" type="text" value={form.tags} onChange={update("tags")} placeholder="oral history, craft" className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="post-author" className={labelClass}>Author</label>
            <input id="post-author" data-testid="post-author-input" type="text" value={form.author} onChange={update("author")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="post-published-at" className={labelClass}>Publish Date (optional — for scheduling)</label>
            <input id="post-published-at" data-testid="post-published-at-input" type="datetime-local" value={form.published_at ? form.published_at.slice(0, 16) : ""} onChange={update("published_at")} className={inputClass} />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 pt-4 border-t border-[#e5e5e5]">
          <button
            type="button"
            onClick={() => save("draft")}
            disabled={busy}
            data-testid="post-save-draft-button"
            className="px-6 py-3 border border-[#121212] text-[#121212] text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] hover:text-white transition-colors rounded-none disabled:opacity-60"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => save("published")}
            disabled={busy}
            data-testid="post-publish-button"
            className="px-6 py-3 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] transition-colors rounded-none disabled:opacity-60"
          >
            {form.published_at && new Date(form.published_at) > new Date() ? "Schedule" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEditPage;
